import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {PageComponent} from 'features/core/shared/mvs-page/page.component';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {PageHeaderConfig} from 'features/core/shared/service/page/page-header-config.interface';
import {
    MvsConfigHeaderComponent
} from 'features/core/shared/components/mvs-config-header/mvs-config-header.component';

/**
 * Model-comparison page (cn/model-comparison) for Geschäftsleitung / Controlling.
 *
 * Hosts two tabs:
 *  - "Forecast" — the existing forward simulation (CnModelComparisonComponent): active model vs. n
 *    scenario models over the same contract base, per-year delta (nominal + NPV/Barwert), async run
 *    via runId polling and an audit-proof decision snapshot. Default tab; unchanged behaviour.
 *  - "Soll/Ist-Abgleich" — the Ist-anchored comparison (CnAbgleichComparisonComponent) driven by the
 *    selection passed from the Abgleich page via query params (statementId, activeModelId,
 *    horizonYears, scenarioModelIds). It calls POST /cn/reconciliation/abgleich and renders the
 *    verdict, Summen-Kacheln, per-contract table and the monthly Verlauf chart.
 *
 * Tab selection: ?tab=abgleich opens the Soll/Ist-Abgleich tab (e.g. from the Abgleich page's
 * "Modell-Abgleich starten" navigation and the "Modell-Vergleich" header link); without it the
 * Forecast tab stays the default so the existing flow is unaffected.
 *
 * Header runs in Component-Mode via MvsConfigHeaderComponent ('Modellvergleich').
 */
@Component({
    selector: 'mvs-cn-model-comparison-page',
    templateUrl: './cn-model-comparison.page.html',
    styleUrls: ['./cn-model-comparison.page.scss'],
    standalone: false
})
export class CnModelComparisonPage extends PageComponent implements OnInit, OnDestroy {

    defaultLabel: string = 'Modellvergleich';

    /** Active top-level tab: 0 = Forecast (default), 1 = Soll/Ist-Abgleich. */
    activeTab: number = 0;

    // ----- Soll/Ist-Abgleich selection (from query params) -----
    abgleichStatementId: number | null = null;
    abgleichActiveModelId: number | null = null;
    abgleichHorizonYears: number = 5;
    abgleichScenarioModelIds: number[] = [];

    private tabParamSubscription: Subscription;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Modellvergleich';
        super.ngOnInit();
        this.subscribeToComparisonParams();
        this.initialized = true;
    }

    override ngOnDestroy(): void {
        this.tabParamSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    /**
     * Read the tab selector and the Soll/Ist-Abgleich selection from the query params. ?tab=abgleich
     * activates the Soll/Ist-Abgleich tab; statementId / activeModelId / horizonYears /
     * scenarioModelIds (comma-separated) feed CnAbgleichComparisonComponent. Without ?tab the default
     * Forecast tab is kept.
     */
    private subscribeToComparisonParams(): void {
        this.tabParamSubscription = this.route.queryParams.subscribe(params => {
            this.activeTab = params['tab'] === 'abgleich' ? 1 : 0;

            this.abgleichStatementId = this.parseNumber(params['statementId']);
            this.abgleichActiveModelId = this.parseNumber(params['activeModelId']);

            const horizon = this.parseNumber(params['horizonYears']);
            this.abgleichHorizonYears = horizon != null && horizon > 0 ? horizon : 5;

            this.abgleichScenarioModelIds = this.parseNumberList(params['scenarioModelIds']);
        });
    }

    /** Parse a single numeric query param; null when absent or not a finite number. */
    private parseNumber(value: string | undefined): number | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    /** Parse a comma-separated numeric query param into a number[] (empty when absent). */
    private parseNumberList(value: string | undefined): number[] {
        if (value === undefined || value === null || value === '') {
            return [];
        }
        return value.split(',')
            .map(part => Number(part.trim()))
            .filter(part => Number.isFinite(part));
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Modellvergleich',
                categoryTypeMode: 'configuration',
                icon: 'fa-light fa-scale-balanced'
            }
        };
    }
}
